import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Users, FolderKanban, BarChart3, LogOut, Menu, X, 
  Heart, Clock, CheckCircle2, Plus, ArrowRight, User, Bell, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ini = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(' ');
  return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const Dashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'casting_manager';

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const projectsRes = await axios.get(`${API}/projects`, { headers, withCredentials: true });
      setRecentProjects(projectsRes.data.slice(0, 5));
      if (isAdmin) {
        const statsRes = await axios.get(`${API}/admin/stats`, { headers, withCredentials: true });
        setStats(statsRes.data);
      }
    } catch (error) { console.error('Dashboard data error:', error); }
    finally { setLoading(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const sidebarItems = [
    { icon: '◈', label: 'Dashboard', path: '/dashboard', badge: null },
    { icon: '◎', label: 'Talent', path: '/talents', badge: null },
    { icon: '▦', label: 'Projects', path: '/projects', badge: null },
    ...(isAdmin ? [{ icon: '⚙', label: 'Admin', path: '/admin', badge: null }] : []),
    { icon: '◻', label: 'Profile', path: '/profile', badge: null },
  ];

  const quickStats = isAdmin && stats ? [
    { label: 'Active Projects', value: stats.projects?.active || 0, sub: recentProjects[0]?.name || 'None' },
    { label: 'Shortlisted', value: stats.talents?.total || 0, sub: '↑ 2 this week', isUp: true },
    { label: 'On Hold', value: stats.bookings?.total || 0, sub: 'Awaiting confirm' },
    { label: 'Confirmed', value: stats.talents?.approved || 0, sub: 'Active', isUp: true },
  ] : [
    { label: 'Active Projects', value: recentProjects.length, sub: recentProjects[0]?.name || 'None' },
    { label: 'Shortlisted', value: 0, sub: '↑ 0 this week', isUp: true },
    { label: 'On Hold', value: 0, sub: 'Awaiting confirm' },
    { label: 'Confirmed', value: 0, sub: 'None yet' },
  ];

  const today = new Date();
  const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
  const dateStr = today.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ background: 'var(--ink)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 nav-glass">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X className="w-5 h-5" style={{ color: 'var(--cream)' }} /> : <Menu className="w-5 h-5" style={{ color: 'var(--cream)' }} />}
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 10, color: '#000' }}>C</span>
            </div>
            <span className="font-display text-base" style={{ color: 'var(--cream)' }}>CASTDECK</span>
          </Link>
          <button className="relative" data-testid="notifications-btn">
            <Bell className="w-5 h-5" style={{ color: 'var(--cream)' }} />
          </button>
        </div>
      </header>

      <div className="dashboard-layout pt-14 lg:pt-0">
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
          <div className="p-5" style={{ borderBottom: '1px solid rgba(255,255,255,.06)' }}>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
                <span style={{ fontFamily: 'var(--display)', fontSize: 14, color: '#000' }}>C</span>
              </div>
              <span className="font-display text-xl" style={{ color: 'var(--cream)', letterSpacing: 3 }}>CASTDECK</span>
            </Link>
          </div>

          <nav className="p-3 flex-1">
            {sidebarItems.map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 mb-0.5 transition-colors text-sm ${
                  location.pathname === item.path
                    ? 'sidebar-link active'
                    : 'hover:bg-[rgba(255,255,255,.03)]'
                }`}
                style={{ 
                  fontFamily: 'var(--head)', fontWeight: 700, fontSize: 12, letterSpacing: 1,
                  color: location.pathname === item.path ? 'var(--gold)' : 'var(--muted-c)'
                }}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="text-base">{item.icon}</span>
                <span>{item.label}</span>
                {item.badge && (
                  <span className="ml-auto text-[9px] px-1.5 py-0.5 font-bold" style={{ background: 'var(--gold)', color: '#000' }}>{item.badge}</span>
                )}
              </Link>
            ))}
          </nav>

          <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,.06)' }}>
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user?.picture ? (
                <img src={user.picture} alt={user?.name} className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 flex items-center justify-center font-display text-sm" style={{ background: 'var(--gold-glow)', color: 'var(--gold)' }}>
                  {user?.name?.charAt(0).toUpperCase() || '?'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>{user?.name}</div>
                <div className="text-[10px] capitalize" style={{ color: 'var(--muted-c)' }}>{user?.role?.replace('_', ' ')}</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 w-full transition-colors text-xs"
              style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)', fontWeight: 700 }}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-content p-5 lg:p-8" data-testid="dashboard-main">
          {/* Dashboard Banner */}
          <div className="relative overflow-hidden mb-5 p-6" style={{ background: 'var(--ink2)', border: '1px solid rgba(255,255,255,.06)' }} data-testid="dash-banner">
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1574867022210-bd9ecc413bf3?w=1200&q=30')", backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-lg font-bold" style={{ fontFamily: 'var(--head)', color: 'var(--cream)' }}>
                  Welcome back, <span style={{ color: 'var(--gold)' }}>{user?.name?.split(' ')[0] || 'User'}</span>
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted-c)' }}>{dayName}, {dateStr} · Your casting snapshot</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => navigate('/talents')} className="btn-ghost px-4 py-2 text-xs" data-testid="browse-talent-btn">Browse Talent</button>
                <button onClick={() => navigate('/projects')} className="btn-gold px-4 py-2 text-xs" data-testid="new-project-btn">+ New Project</button>
              </div>
            </div>
          </div>

          {/* KPI Row */}
          <div className="kpi-row-v3 mb-5" data-testid="kpi-row">
            {quickStats.map((stat, i) => (
              <div key={i} className="kpi-v3" data-testid={`kpi-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <div className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>{stat.label}</div>
                <div className="font-display text-3xl mb-1" style={{ color: 'var(--cream)', letterSpacing: 2 }}>{stat.value}</div>
                <div className="text-[11px]" style={{ color: stat.isUp ? 'var(--green)' : 'var(--muted-c)' }}>{stat.sub}</div>
              </div>
            ))}
          </div>

          {/* Dashboard Panels */}
          <div className="dash-panels-v3">
            {/* Left: Recent Projects */}
            <div style={{ background: 'var(--ink2)', border: '1px solid rgba(255,255,255,.06)' }}>
              <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <span className="text-xs font-bold tracking-wider uppercase" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Recent Projects</span>
                <Link to="/projects" className="text-[10px] font-bold tracking-wider uppercase" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>
                  View All →
                </Link>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full spinner" />
                </div>
              ) : recentProjects.length > 0 ? (
                <div>
                  {recentProjects.map((project, i) => (
                    <Link
                      key={project.project_id}
                      to={`/projects/${project.project_id}`}
                      className="flex items-center justify-between p-4 transition-colors hover:bg-[rgba(255,255,255,.02)]"
                      style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}
                      data-testid={`project-item-${i}`}
                    >
                      <div>
                        <div className="text-sm font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>{project.name}</div>
                        <div className="text-[11px] mt-0.5" style={{ color: 'var(--warm)' }}>{project.project_type} · {project.shortlisted_talents?.length || 0} shortlisted</div>
                      </div>
                      <span className="px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase"
                            style={{ 
                              background: project.status === 'active' ? 'rgba(46,204,138,.1)' : 'var(--faint)',
                              color: project.status === 'active' ? 'var(--green)' : 'var(--muted-c)',
                              border: `1px solid ${project.status === 'active' ? 'rgba(46,204,138,.2)' : 'rgba(255,255,255,.06)'}` 
                            }}>
                        {project.status}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <div className="text-3xl mb-3 opacity-20">▦</div>
                  <p className="text-xs mb-4" style={{ color: 'var(--warm)' }}>No projects yet</p>
                  <button onClick={() => navigate('/projects')} className="btn-gold px-5 py-2 text-xs" data-testid="create-first-project">
                    + Create First Project
                  </button>
                </div>
              )}
            </div>

            {/* Right: Quick Actions + Activity */}
            <div className="flex flex-col gap-4">
              {/* Quick Actions */}
              <div className="p-4 space-y-2" style={{ background: 'var(--ink2)', border: '1px solid rgba(255,255,255,.06)' }}>
                <Link to="/talents" className="flex items-center gap-3 p-3 transition-colors hover:bg-[rgba(255,255,255,.03)]" data-testid="quick-browse-talent">
                  <span className="text-base">◎</span>
                  <div className="flex-1">
                    <div className="text-xs font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Browse Talent</div>
                    <div className="text-[10px]" style={{ color: 'var(--muted-c)' }}>Explore directory</div>
                  </div>
                  <span style={{ color: 'var(--gold)', fontSize: 12 }}>→</span>
                </Link>
                <Link to="/create-profile" className="flex items-center gap-3 p-3 transition-colors hover:bg-[rgba(255,255,255,.03)]" data-testid="quick-add-talent">
                  <span className="text-base">+</span>
                  <div className="flex-1">
                    <div className="text-xs font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Add Talent</div>
                    <div className="text-[10px]" style={{ color: 'var(--muted-c)' }}>Register new profile</div>
                  </div>
                  <span style={{ color: 'var(--gold)', fontSize: 12 }}>→</span>
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-3 p-3 transition-colors hover:bg-[rgba(255,255,255,.03)]" data-testid="quick-admin">
                    <span className="text-base">⚙</span>
                    <div className="flex-1">
                      <div className="text-xs font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Admin Panel</div>
                      <div className="text-[10px]" style={{ color: 'var(--muted-c)' }}>Manage platform</div>
                    </div>
                    <span style={{ color: 'var(--gold)', fontSize: 12 }}>→</span>
                  </Link>
                )}
              </div>

              {/* Activity placeholder */}
              <div className="flex-1 p-4" style={{ background: 'var(--ink2)', border: '1px solid rgba(255,255,255,.06)' }}>
                <div className="text-xs font-bold tracking-wider uppercase mb-4" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Activity</div>
                <div className="text-center py-6 opacity-40">
                  <div className="text-2xl mb-2">📋</div>
                  <div className="text-xs" style={{ color: 'var(--warm)' }}>Activity will appear here</div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Dashboard;
